import { ReactNode } from 'react';
import { Button, Dialog, DialogProps, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { ModalTransition } from './ModalTransition';

export interface ModalProps extends Omit<DialogProps, 'title' | 'onClose' | 'onSubmit'> {
  readonly onClose: (reason: 'backdropClick' | 'escapeKeyDown' | 'closeButtonClick' | 'submitClick') => void;
  readonly title?: ReactNode;
  readonly actions?: ReactNode;
  // Suppresses an accidental dismiss via backdrop click or Escape — only the explicit
  // close/cancel button(s) a caller renders can close it. For a dialog mid async work (the
  // dry-run bench while a run is in flight) losing the poll to a stray click is the defect.
  readonly disableBackdropClose?: boolean;
}

export const Modal = ({
  open,
  maxWidth = 'md',
  fullWidth = true,
  fullScreen,
  scroll = 'body',
  onClose,
  children,
  title,
  actions,
  disableBackdropClose = false,
}: ModalProps) => {
  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (disableBackdropClose && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
          return;
        }

        onClose('backdropClick');
      }}
      disableEscapeKeyDown={disableBackdropClose}
      maxWidth={maxWidth}
      fullScreen={fullScreen}
      scroll={scroll}
      fullWidth={fullWidth}
      TransitionComponent={ModalTransition}
    >
      <Button
        onClick={() => onClose('closeButtonClick')}
        sx={{
          fontSize: 18,
          color: 'text.secondary',
          position: 'absolute',
          right: 18,
          top: 18,
          p: 0.5,
          width: 20,
          minWidth: 20,
          height: 20,
          borderRadius: '50%',
        }}
      >
        <CloseIcon fontSize="inherit" />
      </Button>

      {title && (
        <DialogTitle variant="h2" component="h3">
          {title}
        </DialogTitle>
      )}

      <DialogContent>{children}</DialogContent>

      {actions && <DialogActions>{actions}</DialogActions>}
    </Dialog>
  );
};
